import React, {useState} from 'react';
import { Table } from 'antd';
import { SmallDashOutlined } from '@ant-design/icons';
import { useScreenHeight } from "../../hooks/useScreen";

/**
 * AggregatedTickerTable is a component that displays a table of aggregated ticker data.
 * @param detailedRows - A 2D array of ticker data. The first row should contain column headers.
 *
 * @returns {JSX.Element}
 * @constructor
 */
const CumulativeHoldingsTable = ({ detailedRows }) => {
  const [pageSize, setPageSize] = useState(250);
  // Assuming the first row of detailedRows contains column headers
  const screenHeight = useScreenHeight();
  const columns = detailedRows[0].map((header, index) => {
    // Define a style object based on the column index
    let background = 'transparent';
    let color = 'rgba(0,0,0,0.8)';
    let fontWeight = '500';
    if (index >= 1 && index <= 2) {
      background = '';
       color = '#000';
       fontWeight = '700'
    }

    return {
      title: <span className={'mx-auto text-center'}>{header.toUpperCase()}</span>,
      dataIndex: index.toString(), // Ensure dataIndex is a string for Ant Design's requirements
      key: index.toString(),
      align: index === 0 ? 'left' : 'right', // Align headers and cell content
      sorter: (a, b) => a[index] - b[index],
      sortDirections: ['descend', 'ascend'],
      render: (text, record, rowIndex) => {
        const cellStyle = {
          background, // Apply the background color based on the column index
          fontWeight,
          color,
        };

        if (index === 0) { // First column for row index
          return (
            <div style={cellStyle}>
              {`${rowIndex + 1})  ${detailedRows[rowIndex + 1][0].toUpperCase()}`}
            </div>
          );
        } else {
          if (React.isValidElement(text)) {
            return <div style={cellStyle}>{text}</div>; // If text is already a React component (EllipsisOutlined), return it directly.
          }
          // Format the number as a percentage to two decimal places.
          const formattedPercentage = `${parseFloat(text * 100).toFixed(2)}%`;
          return <div style={cellStyle}>{formattedPercentage}</div>;
        }
      },
    };
  });

  // Initialize an empty array to track the previous values for each column
  let prevValues = Array(detailedRows[0].length).fill(null);

  // Transform detailedRows into dataSource format expected by Ant Design Table
  const dataSource = detailedRows.slice(1).map((row, index) => {
    const rowObj = row.reduce((acc, cell, cellIndex) => {
      // Handling for percentage columns
      if (cellIndex > 1) {
        // Check if this cell's value is identical to the previous cell in the same column
        if (prevValues[cellIndex] === cell) {
          // Store a React component to indicate the value is identical (useful for the render method)
          acc[cellIndex] = <SmallDashOutlined style={{ color: 'gray' }} />;
        } else {
          // Otherwise, store the actual value
          acc[cellIndex] = cell;
        }
        // Update the tracked previous value for this column
        prevValues[cellIndex] = cell;
      } else {
        // For the first column, just store the value (e.g., the row index or identifier)
        acc[cellIndex] = cell;
      }
      return acc;
    }, { key: index });
    return rowObj;
  });

  // Reset prevValues for reuse in rendering logic
  prevValues = Array(detailedRows[0].length).fill(null);

  return (
    <Table
      scroll={{ x: 1000, y: screenHeight - 300 }}
      columns={columns}
      dataSource={dataSource}
      pagination={{ pageSize, pageSizeOptions: [50, 100, 250, 500, 1000] }}
      size={'small'}
    />
  );
};

export default CumulativeHoldingsTable;
